import React, { memo, useCallback, useMemo } from 'react';

import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import cs from 'classnames';

import { useTableContext } from '../../../lib/context/TableContext/TableContext';
import { RowSelectionActions } from '../AgTablePage/components/RowSelectionActions';

import { SkeletonCellRenderer } from './SkeletonCellRenderer';
import CustomDetailCellRenderer from './components/CustomDetailCellRenderer';
import EmptyData from './components/EmptyData';
import { TableHeaderComponent } from './components/TableHeaderComponent';
import CustomLoadingOverlay from './customLoadingOverlay';
import CustomNoRowsOverlay from './customNoRowsOverlay';
import { getActionColumnDef } from './lib/helpers';

import './Table.scss';

LicenseManager.setLicenseKey(process.env.REACT_AG_GRID_KEY);

const AGTable = ({
  data,
  customClassName,
  ExpandedComponent,
  loading,
  skeletonSize = 25,
  actions,
  count,
  onRowClicked,
  onGridReady: proppedOnGridReady = () => {},
  defaultColDef,
  onCellClicked = (event) => event.api.clearFocusedCell(),
  ...props
}) => {
  const {
    onGridReady,
    onColumnMoved,
    colDefs,
    onSortChanged,
    onSelectionChanged,
    pagination,
    gridRef,
    tableLoading,
    limit
  } = useTableContext();
  const isLoading = loading || tableLoading;

  const defaultColDefs = useMemo(
    () => ({
      valueFormatter: ({ value }) => (value == null || value == '' || value == ' ' ? '-' : value),
      headerComponent: TableHeaderComponent,
      ...(defaultColDef || {})
    }),
    [defaultColDef]
  );

  const components = useMemo(
    () => ({
      agLoadingOverlay: CustomLoadingOverlay,
      agNoRowsOverlay: CustomNoRowsOverlay
    }),
    []
  );

  const onRowClickedHandler = useCallback(
    (event) => {
      if (isLoading || typeof onRowClicked !== 'function') return;

      onRowClicked?.(event);
    },
    [isLoading, onRowClicked]
  );

  const onCellClickedHandler = useCallback(
    (event) => {
      if (isLoading || typeof onCellClicked !== 'function') return;

      onCellClicked(event);
    },
    [isLoading, onCellClicked]
  );

  const detailGridOptions = useMemo(
    () => ({
      onFirstDataRendered: (params) => {
        params.api.forEachNode((node) => {
          if (node.expanded) {
            node.setExpanded(false);
            params.api.clearFocusedCell();
          }
        });
      }
    }),
    []
  );

  const handleSortChange = useCallback(
    (event) => {
      if (Number(count) <= Number(limit)) return;
      onSortChanged(event);
    },
    [onSortChanged, count, limit]
  );

  const DetailCellRenderer = useCallback(
    (props) => <CustomDetailCellRenderer {...props} ExpandedComponent={ExpandedComponent} />,
    []
  );

  const onGridReadyHandler = useCallback((props) => {
    onGridReady(props);
    proppedOnGridReady(props);
  }, []);

  const detailCellRendererParams = useMemo(
    () => ({ detailGridOptions, suppressRowClickSelection: true }),
    [detailGridOptions]
  );

  const updatedColDefs = useMemo(() => {
    return colDefs.map((col) => ({
      ...col,
      valueFormatter: (params) => {
        let value;

        if (typeof col?.valueFormatter === 'function') {
          value = col?.valueFormatter(params);
        } else {
          value = params?.value;
        }

        return value == null || value === '' || value === ' ' ? '-' : value;
      },
      cellRendererSelector: isLoading ? () => ({ component: SkeletonCellRenderer }) : undefined,
      ...getActionColumnDef(col?.field)
    }));
  }, [colDefs, isLoading]);

  const updatedRowData = useMemo(() => {
    if (!isLoading) return data;
    return Array.from({ length: skeletonSize }, (_, index) => ({ index }));
  }, [data, isLoading, skeletonSize]);

  return (
    <div
      className={cs(
        'ag-theme-quartz relative h-full overflow-y-hidden',
        pagination && 'ag-pagination',
        customClassName
      )}>
      <AgGridReact
        ref={gridRef}
        onColumnRowGroupChanged={onColumnMoved}
        onColumnMoved={onColumnMoved}
        onColumnPinned={onColumnMoved}
        onColumnVisible={onColumnMoved}
        onGridReady={onGridReadyHandler}
        onSortChanged={handleSortChange}
        rowData={updatedRowData}
        columnDefs={updatedColDefs}
        defaultColDef={defaultColDefs}
        components={components}
        onCellClicked={onCellClickedHandler}
        onRowClicked={onRowClickedHandler}
        onSelectionChanged={onSelectionChanged}
        onRowSelected={onSelectionChanged}
        detailCellRenderer={DetailCellRenderer}
        detailCellRendererParams={detailCellRendererParams}
        noRowsOverlayComponent={EmptyData}
        suppressLoadingOverlay
        {...props}
      />
      <RowSelectionActions actions={actions} />
    </div>
  );
};

export default memo(AGTable);
